(function () {
  'use strict';

  function initGallery(root) {
    var viewport = root.querySelector('[data-wf-gallery-viewport]');
    var track = root.querySelector('[data-wf-gallery-track]');
    if (!viewport || !track) return;

    var itemCount = track.children.length;
    if (itemCount === 0) return;

    var autoplayEnabled = root.dataset.autoplay === 'true';
    var autoplaySpeed = parseFloat(root.dataset.autoplaySpeed) || 60; // px/sec
    var pauseOnHover = root.dataset.pauseOnHover === 'true';
    var showZones = !!(root.querySelector('[data-wf-gallery-prev]'));

    // ---- Infinite loop setup -----------------------------------------
    // No loop: the strip has a real start and end, like supersedia.com.
    // Loop: we clone the whole set once and append it, so the track holds
    // two identical copies back to back. Once scroll passes the width of
    // one copy we silently subtract (or add) that width -- since the two
    // halves are pixel-identical, the jump is invisible and it reads as
    // an endless strip.
    var loopEnabled = root.dataset.loop === 'true' && itemCount > 1;

    if (loopEnabled) {
      var originalItems = Array.prototype.slice.call(track.children);
      originalItems.forEach(function (item) {
        var clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        var focusable = clone.querySelectorAll('a, button, [tabindex]');
        focusable.forEach(function (el) { el.setAttribute('tabindex', '-1'); });
        track.appendChild(clone);
      });
    }

    function loopThreshold() {
      // Width of a single copy of the set (half the doubled track).
      return track.scrollWidth / 2;
    }

    function wrapScroll(value) {
      var threshold = loopThreshold();
      if (threshold <= 0) return value;
      value = value % threshold;
      if (value < 0) value += threshold;
      return value;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - viewport.clientWidth);
    }

    function clampScroll(value) {
      return Math.min(Math.max(value, 0), maxScroll());
    }

    // ---- Momentum (inertia) drag ------------------------------------------
    // On release, the scroll keeps gliding and decelerates smoothly instead
    // of stopping dead where the pointer let go.
    var isDragging = false;
    var dragMoved = false;
    var startX = 0;
    var startScrollLeft = 0;
    var velocity = 0; // px per ms
    var lastMoveX = 0;
    var lastMoveTime = 0;
    var momentumRAF = null;
    // True while a JS-driven smooth scroll (goTo) is in flight, so the
    // native scroll listener below doesn't fight it mid-animation.
    var isSmoothScrolling = false;

    function stopMomentum() {
      if (momentumRAF) {
        cancelAnimationFrame(momentumRAF);
        momentumRAF = null;
      }
    }

    function runMomentum() {
      stopMomentum();
      var friction = 0.94; // higher = glides further, lower = stops sooner

      function step() {
        velocity *= friction;

        if (Math.abs(velocity) < 0.02) {
          momentumRAF = null;
          return;
        }

        var next = viewport.scrollLeft - velocity * 16;
        if (loopEnabled) {
          viewport.scrollLeft = wrapScroll(next);
        } else {
          var clamped = clampScroll(next);
          if (clamped !== next) velocity = 0; // hit an edge, stop gliding
          viewport.scrollLeft = clamped;
        }

        momentumRAF = requestAnimationFrame(step);
      }

      momentumRAF = requestAnimationFrame(step);
    }

    viewport.addEventListener('pointerdown', function (e) {
      stopMomentum();
      isDragging = true;
      dragMoved = false;
      startX = e.clientX;
      startScrollLeft = viewport.scrollLeft;
      lastMoveX = e.clientX;
      lastMoveTime = performance.now();
      velocity = 0;
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture && viewport.setPointerCapture(e.pointerId);
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) dragMoved = true;

      var nextScroll = startScrollLeft - dx;
      viewport.scrollLeft = loopEnabled ? wrapScroll(nextScroll) : clampScroll(nextScroll);

      var now = performance.now();
      var dt = now - lastMoveTime;
      if (dt > 0) {
        var instVelocity = (e.clientX - lastMoveX) / dt; // px/ms, drag direction
        // smooth it a little so a single jumpy sample doesn't dominate
        velocity = velocity * 0.7 + instVelocity * 0.3;
      }
      lastMoveX = e.clientX;
      lastMoveTime = now;
    });

    function endDrag() {
      if (!isDragging) return;
      isDragging = false;
      viewport.classList.remove('is-dragging');
      if (dragMoved && Math.abs(velocity) > 0.02) {
        runMomentum();
      }
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    // Recompute bounds if the window resizes (image widths can change,
    // since gallery height is viewport-relative).
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        viewport.scrollLeft = loopEnabled ? wrapScroll(viewport.scrollLeft) : clampScroll(viewport.scrollLeft);
      }, 150);
    });

    // ---- Native scroll wrap (wheel / trackpad / scrollbar) ----------------
    // Pointer-drag, momentum, autoplay and goTo() all wrap the position
    // themselves. But mouse-wheel / trackpad scrolling, or dragging the
    // (hidden) native scrollbar thumb directly, only ever fires the
    // browser's native "scroll" event -- nothing above was listening for
    // that, so the browser would clamp scrollLeft at the real end of the
    // doubled track and the loop would appear to "stop" after two views.
    // This listener catches every scroll however it was triggered and
    // folds it back into range, so wheel/trackpad scrolling loops too.
    viewport.addEventListener('scroll', function () {
      if (!loopEnabled) return;
      if (isDragging || momentumRAF || isSmoothScrolling) return;
      var wrapped = wrapScroll(viewport.scrollLeft);
      if (wrapped !== viewport.scrollLeft) {
        viewport.scrollLeft = wrapped;
      }
    });

    // ---- Autoplay -----------------------------------------------------
    // Plays forward smoothly. Without loop it simply stops at the end;
    // with loop it wraps around forever.
    var autoplayActive = autoplayEnabled && itemCount > 1;
    var hoverPaused = false;
    var lastTime = null;

    function tick(time) {
      if (lastTime === null) lastTime = time;
      var dt = time - lastTime;
      lastTime = time;

      var atEnd = !loopEnabled && viewport.scrollLeft >= maxScroll() - 1;

      if (autoplayActive && !isDragging && !momentumRAF && !atEnd && !(pauseOnHover && hoverPaused)) {
        var nextPos = viewport.scrollLeft + (autoplaySpeed * dt) / 1000;
        viewport.scrollLeft = loopEnabled ? wrapScroll(nextPos) : clampScroll(nextPos);
      }
      requestAnimationFrame(tick);
    }

    if (autoplayActive) {
      requestAnimationFrame(tick);
      if (pauseOnHover) {
        viewport.addEventListener('mouseenter', function () { hoverPaused = true; });
        viewport.addEventListener('mouseleave', function () { hoverPaused = false; });
        viewport.addEventListener('pointerdown', function () { hoverPaused = true; });
      }
    }

    // ---- Prev / Next click zones + custom cursor ------------------------
    function itemBoundaries() {
      var items = Array.prototype.slice.call(track.children);
      return items.map(function (item) {
        return item.offsetLeft;
      });
    }

    function goTo(direction) {
      stopMomentum();
      var boundaries = itemBoundaries();
      var current = viewport.scrollLeft;
      var target;
      if (direction === 'next') {
        target = boundaries.find(function (b) { return b > current + 5; });
        if (target === undefined) target = loopEnabled ? loopThreshold() : maxScroll();
      } else {
        var candidates = boundaries.filter(function (b) { return b < current - 5; });
        target = candidates.length ? candidates[candidates.length - 1] : 0;
      }
      isSmoothScrolling = true;
      viewport.scrollTo({ left: loopEnabled ? target : clampScroll(target), behavior: 'smooth' });

      if (loopEnabled) {
        // Once the smooth scroll settles, fold the position back into the
        // first copy so there's always a full copy of headroom to scroll into.
        setTimeout(function () {
          isSmoothScrolling = false;
          if (!isDragging) viewport.scrollLeft = wrapScroll(viewport.scrollLeft);
        }, 500);
      } else {
        setTimeout(function () { isSmoothScrolling = false; }, 500);
      }
    }

    if (showZones) {
      var prevZone = root.querySelector('[data-wf-gallery-prev]');
      var nextZone = root.querySelector('[data-wf-gallery-next]');

      [prevZone, nextZone].forEach(function (zone) {
        if (!zone) return;
        var arrow = zone.querySelector('.wf-gallery__arrow');

        zone.addEventListener('mouseenter', function () {
          zone.classList.add('is-hovering');
        });
        zone.addEventListener('mouseleave', function () {
          zone.classList.remove('is-hovering');
        });
        zone.addEventListener('mousemove', function (e) {
          if (!arrow) return;
          arrow.style.left = e.clientX + 'px';
          arrow.style.top = e.clientY + 'px';
        });
        zone.addEventListener('click', function () {
          if (dragMoved) return; // avoid firing a click at the end of a drag
          goTo(zone === prevZone ? 'prev' : 'next');
        });
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-wf-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support: rebuild when blocks are added/reordered
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-wf-gallery]');
    if (root) initGallery(root);
  });
})();
